import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import configData from '../../../../Config/QuestionLevel.json';

const BasicSettings = ({
    title = '',
    QuestionId = 0,
    mark = 0,
    level = '',
    position = false,
    isRelativeToTopic = null,
    isForGame = false,
    typeId,
    type,
    onOpenAiMode,
    onOpenLocalAiModal,
    editorRef,
    onMarkChange,
    onLevelChange,
    onPositionChange,
    onRelativityChange
}) => {
    const { t } = useTranslation('common');
    const questionLevels = configData.QuestionLevel || [];
    const isInitializedRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                if (window.createRedactor && document.querySelector('#QuestionTitle')) {
                    if (!isInitializedRef.current || QuestionId === 0) {
                        window.createRedactor('#QuestionTitle', title || '');
                        isInitializedRef.current = true;
                    }
                }
            } catch (e) {
                console.warn('Redactor init error:', e);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [QuestionId, title]);

    return (
        <div className="step-1-wrapper">
            {/* Question Title & Content Section */}
            <div className="modern-form-section">
                <div className="modern-section-title">
                    <i className="icon fal fa-text"></i>
                    {isForGame
                        ? (t('Pages.Games.forms.LessonInfo.QuestionInfo') || 'معلومات السؤال')
                        : (t('Pages.Lessons.Questions.forms.Info') || 'نص وسؤال الدرس')}
                </div>
                <div className="form-group w-100 mb-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="modern-label mb-0" htmlFor="QuestionTitle">
                            {isForGame
                                ? (t('Pages.Games.forms.LessonInfo.QuestionInfo') || 'نص السؤال:')
                                : (t('Pages.Lessons.Questions.forms.QuestionTitle.title') || 'نص السؤال:')}
                        </label>
                        <button
                            type="button"
                            className="btn-open-ai-chat w-auto"
                            style={{ width: 'auto' }}
                            onClick={() => (onOpenAiMode ? onOpenAiMode() : onOpenLocalAiModal())}
                            title={t('QuestionView.aiChatButtonTooltip') || 'صياغة وتنسيق نص الدرس بالذكاء الاصطناعي'}
                        >
                            <i className="fal fa-sparkles"></i>
                            <span>{t('QuestionView.aiChatButton') || 'شات صياغة نص الدرس ✨'}</span>
                        </button>
                    </div>
                    <textarea
                        id="QuestionTitle"
                        autoComplete="off"
                        placeholder={t('Pages.Lessons.Questions.forms.QuestionTitle.placeholder') || 'أدخل نص السؤال هنا...'}
                        className="form-control"
                        ref={editorRef}
                        defaultValue={title}
                    ></textarea>
                </div>

                {/* Game specific position toggle */}
                {isForGame && (
                    <div className="w-100 mt-4">
                        <label className="modern-label">
                            <i className="icon fal fa-gamepad"></i> {t('Pages.Games.forms.LessonInfo.Type') || 'نوع سؤال اللعبة:'}
                        </label>
                        <div className="toggle-cards-group">
                            <div
                                className={`toggle-card ${position ? 'active-primary' : ''}`}
                                onClick={() => onPositionChange(true)}
                            >
                                <div className="toggle-card-radio">
                                    <div className="toggle-card-radio-inner"></div>
                                </div>
                                <div className="toggle-card-content">
                                    <span className="toggle-card-title">{t('Pages.Games.forms.LessonInfo.IsForFun') || 'للمتعة والترفيه'}</span>
                                </div>
                                <i className="icon fa-regular fa-gamepad-modern toggle-card-icon"></i>
                            </div>
                            <div
                                className={`toggle-card ${!position ? 'active-primary' : ''}`}
                                onClick={() => onPositionChange(false)}
                            >
                                <div className="toggle-card-radio">
                                    <div className="toggle-card-radio-inner"></div>
                                </div>
                                <div className="toggle-card-content">
                                    <span className="toggle-card-title">{t('Pages.Games.forms.LessonInfo.IsQuestion') || 'سؤال تعليمي محتسب'}</span>
                                </div>
                                <i className="icon fa-regular fa-block-question toggle-card-icon"></i>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Marks & Difficulty Level Section (Excluded for Type 11) */}
            {typeId !== 11 && (
                <div className="modern-form-section">
                    <div className="modern-section-title">
                        <i className="icon fal fa-sliders-h"></i>
                        {t('Pages.Lessons.Questions.forms.tools.Settings') || 'إعدادات العلامات والصعوبة'}
                    </div>
                    <div className="modern-grid">
                        <div className="form-group mb-0 w-100">
                            <label className="modern-label" htmlFor="Mark">
                                <i className="icon fas fa-tally"></i>
                                {position
                                    ? (t('Pages.Games.forms.LessonInfo.TryCount') || 'عدد المحاولات')
                                    : (t('Pages.Lessons.Questions.forms.QuestionMark.title') || 'العلامة:')}
                            </label>
                            <div className="modern-input-wrapper">
                                <input
                                    id="Mark"
                                    name="Mark"
                                    value={mark || ''}
                                    type="number"
                                    min="1"
                                    required
                                    autoComplete="off"
                                    className="form-control"
                                    onChange={(e) => onMarkChange(e.target.value)}
                                    placeholder={
                                        position
                                            ? (t('Pages.Games.forms.LessonInfo.TryCountInfo') || 'أدخل عدد المحاولات')
                                            : (t('Pages.Lessons.Questions.forms.QuestionMark.placeholder') || 'أدخل علامة السؤال')
                                    }
                                />
                            </div>
                        </div>
                        <div className="form-group mb-0 w-100">
                            <label className="modern-label" htmlFor="Level">
                                <i className="icon fas fa-water-rise"></i>
                                {t('Pages.Lessons.Questions.forms.QuestionLevel.title') || 'مستوى الصعوبة:'}
                            </label>
                            <div className="modern-input-wrapper">
                                <select
                                    id="Level"
                                    className="form-control"
                                    required
                                    value={level || ''}
                                    onChange={(e) => {
                                        const selectedIndex = e.target.selectedIndex;
                                        const parsedLevel = parseInt(e.target.value, 10);
                                        const levelName = e.target[selectedIndex]?.text || '';
                                        onLevelChange(parsedLevel, levelName);
                                    }}
                                    placeholder={t('Pages.Lessons.Questions.forms.QuestionLevel.placeholder')}
                                >
                                    {questionLevels.map((lvl) => (
                                        <option key={lvl.value} value={lvl.value}>
                                            {lvl.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Layout Positioning & Text Relativity Section */}
            {!(isForGame || type === 'SelfDevelopment') && (
                <div className="modern-form-section">
                    <div className="modern-section-title">
                        <i className="icon fal fa-laptop-code"></i>
                        {t('Pages.Lessons.Questions.forms.tools.LayoutAndRelativity') || 'تنسيق العرض والارتباط'}
                    </div>

                    <div className="w-100 mb-4">
                        <label className="modern-label">
                            <i className="icon fal fa-arrows-h"></i> {t('Pages.Lessons.Questions.forms.tools.position') || 'موضع السؤال في الصفحة:'}
                        </label>
                        <div className="toggle-cards-group">
                            <div
                                className={`toggle-card ${position ? 'active-success' : ''}`}
                                onClick={() => onPositionChange(true)}
                            >
                                <div className="toggle-card-radio">
                                    <div className="toggle-card-radio-inner"></div>
                                </div>
                                <div className="toggle-card-content">
                                    <span className="toggle-card-title">{t('Pages.Lessons.Questions.forms.tools.positionRight') || 'الجانب الأيمن'}</span>
                                    <span className="toggle-card-desc">{t('Pages.Lessons.Questions.forms.tools.positionRightDesc') || 'عرض السؤال على الجانب الأيمن'}</span>
                                </div>
                                <i className="icon fad fa-arrow-alt-from-left toggle-card-icon"></i>
                            </div>
                            <div
                                className={`toggle-card ${!position ? 'active-success' : ''}`}
                                onClick={() => onPositionChange(false)}
                            >
                                <div className="toggle-card-radio">
                                    <div className="toggle-card-radio-inner"></div>
                                </div>
                                <div className="toggle-card-content">
                                    <span className="toggle-card-title">{t('Pages.Lessons.Questions.forms.tools.positionLeft') || 'الجانب الأيسر'}</span>
                                    <span className="toggle-card-desc">{t('Pages.Lessons.Questions.forms.tools.positionLeftDesc') || 'عرض السؤال على الجانب الأيسر'}</span>
                                </div>
                                <i className="icon fad fa-arrow-alt-from-right toggle-card-icon"></i>
                            </div>
                        </div>
                    </div>

                    <div className="w-100">
                        <label className="modern-label">
                            <i className="icon fal fa-sitemap"></i> {t('QuestionView.isRelativeToTopic') || 'ارتباط السؤال بالنص (تحديد نوع الارتباط):'}
                        </label>
                        <div className="toggle-cards-group">
                            <div
                                className={`toggle-card ${isRelativeToTopic === true ? 'active-info' : ''}`}
                                onClick={() => onRelativityChange(true)}
                            >
                                <div className="toggle-card-radio">
                                    <div className="toggle-card-radio-inner"></div>
                                </div>
                                <div className="toggle-card-content">
                                    <span className="toggle-card-title">{t('QuestionView.general') || 'عام (مستقل)'}</span>
                                    <span className="toggle-card-desc">{t('QuestionView.generalOption') || 'لا يتطلب قراءة نص الدرس للإجابة عليه'}</span>
                                </div>
                                <i className="icon fal fa-shield-check toggle-card-icon"></i>
                            </div>
                            <div
                                className={`toggle-card ${isRelativeToTopic === false ? 'active-info' : ''}`}
                                onClick={() => onRelativityChange(false)}
                            >
                                <div className="toggle-card-radio">
                                    <div className="toggle-card-radio-inner"></div>
                                </div>
                                <div className="toggle-card-content">
                                    <span className="toggle-card-title">{t('QuestionView.relativeToText') || 'مرتبط بالنص'}</span>
                                    <span className="toggle-card-desc">{t('QuestionView.relativeToTextOption') || 'يتطلب قراءة نص الدرس للإجابة عليه'}</span>
                                </div>
                                <i className="icon fal fa-book-reader toggle-card-icon"></i>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(BasicSettings);
